import { AangevraagdeVestiging, Vestiging } from "@/entities/vestiging";
import { VestigingStatus } from "@/entities/vestiging/constants";
import { Button } from "@/shared/components";
import { ErrorDisplay } from "@/shared/components/error";
import { logError } from "@/shared/utils/datadog-logger";
import { useCallback, useState } from "react";
import { VestigingenUpdateHandler } from "../../shared";
import { BulkApprovalDialog } from "./bulk-approval-dialog";
import { FaciliteitenDetailsForStation } from "./faciliteiten-details-for-station";

interface NsRetailVestigingenSectionProps {
    variant: "start" | "stop";
    nsRetailVestigingenByStation: Record<string, Vestiging[]>;
    onVestigingenUpdate: VestigingenUpdateHandler;
    onToelichtingChange: React.Dispatch<React.SetStateAction<Record<number, string>>>;
}

export function NsRetailVestigingenSection({
    variant,
    nsRetailVestigingenByStation,
    onVestigingenUpdate,
    onToelichtingChange,
}: Readonly<NsRetailVestigingenSectionProps>) {
    const [globalBulkDialogOpen, setGlobalBulkDialogOpen] = useState<"start" | "stop" | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const vestigingenByStation = Object.fromEntries(
        Object.entries(nsRetailVestigingenByStation)
            .map(([stationCode, vestigingen]): [string, Vestiging[]] => [
                stationCode,
                vestigingen.filter(
                    (v) =>
                        v.aangevraagdeVestiging!.status ===
                        (variant === "start" ? VestigingStatus.BESTELD : VestigingStatus.STOP_AANGEVRAAGD)
                ),
            ])
            .filter(([, vestigingen]) => vestigingen.length > 0)
    );

    const handleBulkOperation = async (status: VestigingStatus, errorMessage: string) => {
        setIsLoading(true);
        setError(null);
        try {
            const vestigingen = Object.values(vestigingenByStation).flat();
            const updates = vestigingen.reduce((acc: Record<number, AangevraagdeVestiging>, vestiging) => {
                acc[vestiging.aangevraagdeVestiging!.id] = AangevraagdeVestiging.update(vestiging.aangevraagdeVestiging!, {
                    status,
                    toelichtingKlantenservice: vestiging.aangevraagdeVestiging!.toelichtingKlantenservice,
                });
                return acc;
            }, {});

            await onVestigingenUpdate(updates);
            setGlobalBulkDialogOpen(null);
            onToelichtingChange({});
        } catch (err) {
            logError(errorMessage, err as Error);
            setError(new Error(errorMessage));
        } finally {
            setIsLoading(false);
        }
    };

    const handleBulkApprove = () => handleBulkOperation(VestigingStatus.ACTIEF, "Er ging iets mis bij het goedkeuren van de vestigingen");

    const handleBulkStop = () => handleBulkOperation(VestigingStatus.GESTOPT, "Er ging iets mis bij het stoppen van de vestigingen");

    const handleToelichtingChange = useCallback(
        (vestiging: Vestiging, toelichting: string) => {
            onToelichtingChange((prev) => ({
                ...prev,
                [vestiging.aangevraagdeVestiging!.id]: toelichting,
            }));
        },
        [onToelichtingChange]
    );

    return (
        <div className="space-y-4">
            {error && <ErrorDisplay error={error} />}
            <h4 className="text-xl font-medium">AXI vestigingen {variant === "start" ? "starten" : "stoppen"}</h4>
            {Object.entries(vestigingenByStation).map(([stationCode, vestigingen]) => {
                const filteredVestigingen = vestigingen.filter(
                    (v) =>
                        v.aangevraagdeVestiging!.status ===
                        (variant === "start" ? VestigingStatus.BESTELD : VestigingStatus.STOP_AANGEVRAAGD)
                );

                return (
                    <FaciliteitenDetailsForStation
                        key={stationCode}
                        variant={variant === "start" ? "startNsRetailVestiging" : "stopNsRetailVestiging"}
                        stationCode={stationCode}
                        vestigingen={filteredVestigingen}
                        onVestigingUpdate={onVestigingenUpdate}
                        onLocalToelichtingChange={handleToelichtingChange}
                    />
                );
            })}
            <div className="flex items-center justify-end">
                <Button
                    variant="secondary"
                    destructive={variant === "stop"}
                    onClick={() => setGlobalBulkDialogOpen(variant)}
                    disabled={isLoading}
                >
                    {`Vestigingen ${variant === "start" ? "goedgekeurd" : "gestopt"} in AXI`}
                </Button>
            </div>
            {!!globalBulkDialogOpen && (
                <BulkApprovalDialog
                    onOpenChange={(open) => setGlobalBulkDialogOpen(open ? globalBulkDialogOpen : null)}
                    operation={globalBulkDialogOpen}
                    onConfirm={() => {
                        if (globalBulkDialogOpen === "start") {
                            void handleBulkApprove();
                        } else {
                            void handleBulkStop();
                        }
                    }}
                />
            )}
        </div>
    );
}
